import { useMemo } from "react";
import {
  fieldGradeContribution,
  formatFieldDisplayValue,
  getFieldLabel,
  getGradeColor,
  getGradeForeground,
  isLayoutField,
  isValueEmpty,
  type NormalizedField,
  type RuntimeScope,
} from "./runtimeModel";

const BINARY_TYPES = new Set(["signature", "file", "photo", "pdf-upload"]);

function displayAnswer(field: NormalizedField, value: unknown): string {
  if (isValueEmpty(value)) return "—";
  if (BINARY_TYPES.has(field.effective_field_type)) {
    if (field.effective_field_type === "signature") return "Signatur vorhanden";
    if (field.effective_field_type === "photo") return "Foto vorhanden";
    return "Datei vorhanden";
  }
  const text = formatFieldDisplayValue(field, value);
  if (text.length > 400) return "Angabe vorhanden";
  return text;
}

export function ReadonlyMaintenance({
  fields,
  values,
  grade,
  autoGrade,
  manualGrade,
  overrideReason,
}: {
  fields: NormalizedField[];
  values: Record<RuntimeScope, Record<string, unknown>>;
  grade: number | null;
  autoGrade: number | null;
  manualGrade: number | null;
  overrideReason: string;
}) {
  const answers = useMemo(() => {
    return fields
      .filter((field) => field.effective_scope === "inspection")
      .filter((field) => field.resolved_visible)
      .filter((field) => !isLayoutField(field.effective_field_type))
      .map((field) => {
        const value = values[field.effective_scope]?.[field.field_key];
        const contribution = fieldGradeContribution(field, value);
        return {
          key: field.field_key,
          label: getFieldLabel(field),
          text: displayAnswer(field, value),
          empty: isValueEmpty(value),
          contribution,
        };
      })
      .filter((entry) => !entry.empty || entry.contribution?.explainsGrade);
  }, [fields, values]);

  const reasons = answers.filter((entry) => entry.contribution?.explainsGrade);
  const determiningGrade = reasons.reduce<number | null>((worst, entry) => {
    const next = entry.contribution?.grade ?? null;
    if (next == null) return worst;
    return worst == null ? next : Math.max(worst, next);
  }, null);
  const shownGrade = grade ?? determiningGrade;
  const corrected = manualGrade != null && autoGrade != null && manualGrade !== autoGrade;

  return (
    <div className="space-y-6">
      <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h3 className="text-base font-semibold text-label">Bewertung</h3>
        <p className="mt-1 text-sm text-label-secondary">Die markierten Antworten zeigen, woher die Note kommt.</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {shownGrade != null ? (
            <span
              className="inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold"
              style={{ backgroundColor: getGradeColor(shownGrade), color: getGradeForeground(shownGrade) }}
            >
              Note {shownGrade}
            </span>
          ) : (
            <span className="text-sm text-label-secondary">Keine Note</span>
          )}
          {corrected ? (
            <span className="text-sm text-label-secondary">
              Berechnet war Note {autoGrade}, gespeichert ist Note {manualGrade}.
            </span>
          ) : null}
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-semibold text-label">Warum diese Note</h4>
          {reasons.length ? (
            <ul className="mt-2 space-y-2">
              {reasons.map((entry) => {
                const contribution = entry.contribution!;
                const determines = determiningGrade != null && contribution.grade === determiningGrade;
                return (
                  <li
                    key={entry.key}
                    className="rounded-xl border px-3 py-2"
                    style={{
                      borderColor: getGradeColor(contribution.grade),
                      backgroundColor: `${getGradeColor(contribution.grade)}14`,
                    }}
                  >
                    <div className="text-sm font-medium text-label">{entry.label}</div>
                    <div className="mt-0.5 text-sm text-label">
                      Antwort: {contribution.answerLabel || entry.text}
                    </div>
                    <div className="mt-1 text-xs font-semibold" style={{ color: getGradeColor(contribution.grade) }}>
                      Ergibt Note {contribution.grade}
                      {determines ? " · bestimmt die Gesamtnote" : ""}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-label-secondary">
              {shownGrade != null && shownGrade <= 1
                ? "Keine Antwort hat einen Mangel ausgelöst. Deshalb ist die Note 1."
                : "Aus den gespeicherten Antworten lässt sich kein einzelner Mangel mehr zuordnen."}
            </p>
          )}
          {overrideReason ? (
            <p className="mt-3 text-sm text-label-secondary">
              <span className="font-medium text-label">Notenkorrektur:</span> {overrideReason}
            </p>
          ) : null}
        </div>
      </section>

      <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <h3 className="mb-3 text-base font-semibold text-label">Fragen und Antworten</h3>
        {answers.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {answers.map((entry) => {
              const accent = entry.contribution?.explainsGrade ? getGradeColor(entry.contribution.grade) : null;
              return (
                <article
                  key={entry.key}
                  className="rounded-xl border border-separator bg-fill-tertiary/40 px-3 py-2.5"
                  style={
                    accent
                      ? { borderColor: accent, boxShadow: `inset 3px 0 0 ${accent}` }
                      : undefined
                  }
                >
                  <div className="text-xs font-semibold" style={{ color: accent || undefined }}>
                    <span className={accent ? "" : "text-label-secondary"}>{entry.label}</span>
                  </div>
                  <div className="mt-1 text-sm text-label">{entry.text}</div>
                  {entry.contribution?.explainsGrade ? (
                    <div className="mt-1 text-xs font-semibold" style={{ color: accent || undefined }}>
                      Diese Antwort ergibt Note {entry.contribution.grade}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-label-secondary">Zu dieser Wartung sind keine Antworten gespeichert.</p>
        )}
      </section>
    </div>
  );
}
